<script setup lang="ts">
import { ref, storeToRefs, useForm, watch } from '#imports';
import { toTypedSchema } from '@vee-validate/yup';
import * as Yup from 'yup';

import type { AuthTypes } from './../types';

import { useDialogResponseStore } from '@/widgets/dialog-response/store';
import { useUserDialogStore } from './../store/dialog';
import { userSignService, userInfoService } from './../api';
import { CaptchaProcessEnum, useCaptchaStore } from '@/features/captcha/store';

import { errorValidationMessages } from '@/app/messages';
import { removeKeyObject } from '@/shared/utils/remove-key-object';
import { Validation } from '@/app/rules';

const dialogResponseStore = useDialogResponseStore();
const { setResponseModalMessage, showResponseModal } = dialogResponseStore;

const { closeUserDialog } = useUserDialogStore();

const { defineField, handleSubmit, handleReset, errors } = useForm({
	validationSchema: toTypedSchema(
		Yup.object().shape({
			email: Yup.string().email(errorValidationMessages.required.email).required(errorValidationMessages.required.email),
			country: Yup.number().required(errorValidationMessages.required.country),
			city_name: Yup.string().required(errorValidationMessages.required.city),
			password: Yup.string()
				.min(Validation.Password.minLength, errorValidationMessages.regex.password)
				.required(errorValidationMessages.required.password),
			passwordRepeat: Yup.string()
				.required(errorValidationMessages.required.passwordRepeat)
				.oneOf([Yup.ref('password')], errorValidationMessages.sameAs.passwordRepeat),
		}),
	),
});

const [emailModel, emailAttrs] = defineField('email');
const [countryModel] = defineField('country');
const [cityModel, cityAttrs] = defineField('city_name');
const [passwordModel, passwordAttrs] = defineField('password');
const [passwordRepeatModel, passwordRepeatAttrs] = defineField('passwordRepeat');

const isLoading = ref(false);
const errorResponse = ref<null | Record<string, Array<string>>>(null);

/** Handle Submit */
const captchaStore = useCaptchaStore();
const { captchaToken, captchaProcess } = storeToRefs(captchaStore);
const { captchaExecute, captchaReset } = captchaStore;

const payload = ref();

const onSubmit = handleSubmit((model) => {
	payload.value = model;
	captchaExecute(CaptchaProcessEnum.Register);
});

watch(captchaToken, async (val) => {
	if (val.length  && captchaProcess.value === CaptchaProcessEnum.Register) {
		errorResponse.value = null;
		isLoading.value = true;

		const purePayload = removeKeyObject(payload.value, (key: any) => key === 'passwordRepeat');

		const response = await userSignService.register({
			...purePayload,
			captcha_token: val,
		} as AuthTypes.RegisterPayload);

		isLoading.value = false;
		captchaReset();

		if ('error' in response) {
			errorResponse.value = response.error;
			return;
		}

		handleReset();
		setResponseModalMessage(response.detail);
		setTimeout(() => showResponseModal(), 0);
		closeUserDialog();
	}
});
</script>

<template>
	<form class="form" @submit.prevent="onSubmit">
		<div class="form__block">
			<div class="form__field">
				<v-input
					v-model="emailModel"
					v-bind="emailAttrs"
					type="email"
					name="email"
					placeholder="Email"
					required
					:is-error="!!errors.email"
					:error-message="errors.email"
				/>
			</div>

			<div class="form__field">
				<v-input-search
					placeholder="Страна"
					:variations="['absolute']"
					:mapper-options="['country_id', 'title_ru']"
					:fetch-func="userInfoService.fetchCountry"
					:is-error="!!errors.country"
					:error-message="errors.country"
					@pick="(item: any) => countryModel = item.id"
					@reset-value="countryModel = undefined"
				/>
			</div>

			<div class="form__field">
				<v-input
					v-model="cityModel"
					v-bind="cityAttrs"
					:disabled="!countryModel"
					type="text"
					name="city"
					placeholder="Город"
					required
					:is-error="!!errors.city_name"
					:error-message="errors.city_name"
				/>
			</div>

			<div class="form__field">
				<v-input
					v-model="passwordModel"
					v-bind="passwordAttrs"
					type="password"
					name="NEW_PASSWORD"
					placeholder="Пароль"
					required
					:is-error="!!errors.password"
					:error-message="errors.password"
				/>
			</div>

			<div class="form__field">
				<v-input
					v-model="passwordRepeatModel"
					v-bind="passwordRepeatAttrs"
					type="password"
					name="PASSWORD"
					placeholder="Повторите пароль"
					required
					:is-error="!!errors.passwordRepeat"
					:error-message="errors.passwordRepeat"
				/>
			</div>
		</div>

		<div class="form__block">
			<v-button
				type="submit"
				:disabled="isLoading"
				class="form__submit"
			>
				Зарегистрироваться
			</v-button>
		</div>

		<div
			v-if="errorResponse !== null"
			class="form__block form__block--error"
		>
			<p
				v-for="(error, key) in errorResponse"
				:key="key"
				class="text-small mb-8"
			>
				<span v-for="(item, index) in error" :key="`${key}-${index}`">
					{{ key }} - {{ item }}
				</span>
			</p>
		</div>
	</form>
</template>
